import { createStoreon } from 'storeon';
import { storeonDevtools, storeonLogger } from 'storeon/devtools';

import { AppEvents, appModule, AppState } from './modules/app';
import { BiEvents, biModule, BiState } from './modules/bi';
import { ChatbotEvents, chatbotModule, ChatbotState } from './modules/chatbot';
import { ContextEvents, contextModule, ContextState } from './modules/context';
import { TooltipEvents, tooltipModule, TooltipReducer as TooltipState } from './modules/tooltip';
import { WidgetEvents, widgetModule, WidgetState } from './modules/widget';

export type ItemPosition = {
  left: number;
  top: number;
  bottom: number;
  right: number;
};

export type State = AppState & BiState & ChatbotState & WidgetState & TooltipState & ContextState;

export type Events = AppEvents & BiEvents & ChatbotEvents & WidgetEvents & TooltipEvents & ContextEvents;

type TKey = keyof State;
type TConnectCallback = (state: State) => void;
type TConnect = (key: any, cb: TConnectCallback) => () => void;

interface ISubscriber {
  key: TKey;
  cb: TConnectCallback;
}

let subscribers: ISubscriber[] = [];

export const store = createStoreon<State, Events>([
  appModule,
  biModule,
  chatbotModule,
  tooltipModule,
  widgetModule,
  contextModule,
  ...(process.env.NODE_ENV === 'development' ? [storeonDevtools, storeonLogger] : []),
]);

const { on, get, dispatch } = store;

const connect: TConnect = (key, cb) => {
  subscribers.push({ key, cb });

  return () => {
    subscribers = subscribers.filter((s) => s.cb !== cb);
  };
};

const bind: TConnect = (key, cb) => {
  requestAnimationFrame(() => {
    cb(get());
  });

  return connect(key, cb);
};

on('@changed', (state, diff) => {
  subscribers.forEach((subscriber) => {
    if (subscriber.key in diff) {
      subscriber.cb(state);
    }
  });
});

export { bind, connect, dispatch, get as getState, on as onStoreEvent };
